import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';

import { makeGetProject } from '../selectors/projects'
import { makeGetProjectUsers, makeGetResolvedNonGuestProjectUsers } from '../selectors/users'
import { makeGetCurrentTeam } from '../selectors/teams'

import { useCanUser } from 'shared-hooks/permission';
import { useCanViewOtherUsers } from 'shared-hooks/project';

import UsersList from './users-list';

const ProjectUsersList = ({ projectId, projectOwner, allUsers, nonClientUsers, teamOwnerId, canAddUser = true, size = 'small', className = '', history, location, project, t }) => {
    const canInviteToProject = useCanUser('inviteToProject', projectOwner);
    const canViewProjectClients = useCanUser('viewProjectClients', projectOwner);
    const canViewOtherUsers = useCanViewOtherUsers(projectId);

    const users = useMemo(() => {
        let result;

        if (canViewProjectClients) {
            result = allUsers || [];
        } else {
            result = nonClientUsers || [];
        }

        return [...new Set([teamOwnerId, ...result])];
    }, [teamOwnerId, allUsers, nonClientUsers]);

    const handleAddUserClick = useCallback(() => {
        history.push({
            pathname: `/settings/project/${projectId}/people/invite`,
            state: {
                closeUrl: location.pathname
            }
        });
    }, [history, projectId, location?.pathname]);

    if (!canViewOtherUsers) {
        return null;
    }

    return (
        <UsersList
            users={users}
            size={size}
            className={className}
            maxUsersCount={10}
            border={true}
            addUserTooltip={t('invite.Invite Members to Project')}
            onAddUserClick={canAddUser && canInviteToProject ? handleAddUserClick : undefined}
        />
    );
}

const makeMapStateToProps = () => {
    const getProject = makeGetProject();
    const getProjectUsers = makeGetProjectUsers();
    const getCurrentTeam = makeGetCurrentTeam();
    const getResolvedNonGuestProjectUsers = makeGetResolvedNonGuestProjectUsers();

    return ({ projects, teams, users }, ownProps) => {
        const project = getProject(projects.items, ownProps.projectId);
        const teamOwnerId = getCurrentTeam(teams).owner;

        return {
            projectOwner: project?.owner,
            teamOwnerId,
            allUsers: project && getProjectUsers(project),
            nonClientUsers: project && getResolvedNonGuestProjectUsers(project, users).map(({ id }) => id)
        }
    }
}

export default withRouter(connect(makeMapStateToProps)(withTranslation('settings')(ProjectUsersList)));
